// src/modules/myvault/components/automations/actions/UpdateFieldAction.tsx

// What? UPDATE_FIELD editor: view + optional row-ref expression + field
//       select + value expression (literal or {{field}} ref).
// Why?  The dispatcher accepts `{view_id, row_id?, field, value}`; a typed
//       form plus column-aware field select avoids typos and invalid column
//       names.

import React from "react";
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
} from "@mui/joy";
import { useViews } from "../../../hooks/useViews";
import type { ActionEditorProps } from "../types";

const UpdateFieldAction: React.FC<ActionEditorProps> = ({
  config,
  onChange,
  context,
}) => {
  const { views } = useViews(context.workspaceId);
  const viewId = typeof config.view_id === "string" ? config.view_id : "";
  const rowId = typeof config.row_id === "string" ? config.row_id : "";
  const field = typeof config.field === "string" ? config.field : "";
  const value =
    config.value === undefined || config.value === null
      ? ""
      : String(config.value);

  return (
    <Stack spacing={2}>
      <FormControl>
        <FormLabel>Target view</FormLabel>
        <Select
          size="sm"
          value={viewId}
          onChange={(_, val) => onChange({ view_id: (val as string) || "" })}
          placeholder="Select a view"
        >
          <Option value="">None</Option>
          {views.map((v) => (
            <Option key={v.id} value={String(v.id)}>
              {v.name}
            </Option>
          ))}
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel>Row reference (optional)</FormLabel>
        <Input
          size="sm"
          value={rowId}
          onChange={(e) => onChange({ row_id: e.target.value })}
          placeholder="Leave blank for triggering row or use {{fk_column}}"
        />
        <FormHelperText>
          If blank, the action targets the row that fired the rule. Use{" "}
          <code>{"{{column}}"}</code> to follow a foreign-key reference.
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel>Field</FormLabel>
        <Select
          size="sm"
          value={field}
          onChange={(_, val) => onChange({ field: (val as string) || "" })}
          placeholder={
            context.columns.length === 0
              ? "Pick a target table first"
              : "Select a column"
          }
          disabled={context.columns.length === 0}
        >
          {context.columns.map((c) => (
            <Option key={c.id} value={c.column_name}>
              {c.display_name || c.column_name}
            </Option>
          ))}
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel>Value</FormLabel>
        <Input
          size="sm"
          value={value}
          onChange={(e) => onChange({ value: e.target.value })}
          placeholder="Literal, or {{field_name}} for a reference"
        />
      </FormControl>
    </Stack>
  );
};

export default UpdateFieldAction;
